/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, ReactNode, useContext } from 'react';

import api from '../services/api';
import { Category } from '../models/Category';
import { Product, Tag } from '../models/Product';
import { DeliveryMethod } from '../models/Order';
import { Discount, DiscountType } from '../models/Discount';

// Definicja interfejsu dla danych przy tworzeniu nowego rabatu
interface CreateDiscountData {
  type: DiscountType;
  value: number;
  startTime?: Date | null;
  endTime?: Date | null;
  isLimited: boolean;
}

interface GetProductsParams {
  draft?: boolean;
  page?: number;
  limit?: number;
  query?: string;
  categoryId?: number | null;
  minPrice?: number;
  maxPrice?: number;
  filters?: Record<number, string[]>;
  producers?: string[];
  tag?: Tag;
  order?: 'asc' | 'desc';
  orderBy?: 'date' | 'price';
  producerIds?: number[];
}

interface ProductContextType {
  createProduct: (formData: FormData) => Promise<void>;
  editProduct: (
    id: number,
    deletedSecondaryImages: number[],
    formData: FormData,
  ) => Promise<void>;
  getProducts: (params: GetProductsParams) => Promise<{
    products: Product[];
    totalProducts: number;
    totalPages: number;
    currentPage: number;
    maxPrice: number;
    minPrice: number;
  }>;
  getProduct: (id: number) => Promise<{
    product: Product;
    categoryAncestry: Category[];
  }>;
  getProductByName: (name: string) => Promise<{
    product: Product;
    categoryAncestry: Category[];
    cheapestDelivery: {
      deliveryCost: number;
      deliveryMethod: DeliveryMethod;
    };
  }>;
  deleteProduct: (id: number) => Promise<void>;
  getPopularProducts: () => Promise<Product[]>;
  getProductsForProducer: (name: string) => Promise<Product[]>;
  getProductsWithMissingDeliveryCosts: () => Promise<Product[]>;
  
  // Zmodyfikowane metody do obsługi rabatów
  getProductDiscounts: (productId: number) => Promise<Discount[]>;
  createDiscount: (productId: number, discountData: CreateDiscountData) => Promise<Discount>;
  deactivateDiscount: (discountId: number) => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({
  children,
}) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('/products/', reactAppApiUrl).toString();

  const deleteProduct = async (id: number) => {
    try {
      await api.delete(`${apiUrl}product/${id}`);
    } catch (error: any) {
      console.error('Failed to delete Product', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getProductsForProducer = async (name: string): Promise<Product[]> => {
    try {
      const response = await api.get(`${apiUrl}producer/${encodeURI(name)}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch Products for Producer', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getPopularProducts = async (): Promise<Product[]> => {
    try {
      const response = await api.get(`${apiUrl}popular`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch Popular Products', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getProducts = async (
    args: GetProductsParams = {},
  ): Promise<{
    products: Product[];
    totalProducts: number;
    totalPages: number;
    currentPage: number;
    maxPrice: number;
    minPrice: number;
  }> => {
    try {
      const serializedFilters = JSON.stringify(args.filters);
      const serializedProducers = JSON.stringify(args.producers);

      const response = await api.get(`${apiUrl}`, {
        params: {
          filters: serializedFilters,
          producers: serializedProducers,
          search: args.query,
          ...args,
        },
      });

      return {
        products: response.data.products,
        totalProducts: response.data.totalProducts,
        totalPages: response.data.totalPages,
        currentPage: response.data.currentPage || 1,
        maxPrice: response.data.maxPrice,
        minPrice: response.data.minPrice,
      };
    } catch (error: any) {
      console.error('Failed to fetch Products', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getProduct = async (
    id: number,
  ): Promise<{
    product: Product;
    categoryAncestry: Category[];
  }> => {
    try {
      const response = await api.get(`${apiUrl}product/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch Product', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getProductByName = async (
    name: string,
  ): Promise<{
    product: Product;
    categoryAncestry: Category[];
    cheapestDelivery: {
      deliveryCost: number;
      deliveryMethod: DeliveryMethod;
    };
  }> => {
    try {
      const response = await api.get(`${apiUrl}product/name/${name}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch Product', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const createProduct = async (formData: FormData) => {
    console.log(formData);
    try {
      await api.post(`${apiUrl}product`, formData);
    } catch (error: any) {
      console.error('Failed to create Product', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const editProduct = async (
    id: number,
    deletedSecondaryImages: number[],
    formData: FormData,
  ) => {
    try {
      await api.put(`${apiUrl}product/${id}`, {
        ...formData,
        deletedSecondaryImages,
      });
    } catch (error: any) {
      console.error('Failed to edit Product', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Get products with missing delivery costs
  const getProductsWithMissingDeliveryCosts = async (): Promise<Product[]> => {
    try {
      const response = await api.get(
        new URL('missing-delivery-cost', apiUrl).toString(),
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Implementacja metod do obsługi rabatów
  
  // Pobieranie rabatów dla produktu
  const getProductDiscounts = async (productId: number): Promise<Discount[]> => {
    try {
      const response = await api.get(`${apiUrl}product/${productId}/discounts`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch product discounts', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Tworzenie nowego rabatu dla produktu
  const createDiscount = async (productId: number, discountData: CreateDiscountData): Promise<Discount> => {
    try {
      const response = await api.post(`${apiUrl}product/${productId}/discount`, {
        ...discountData,
        // Upewniamy się, że daty są sformatowane jako ISO string jeśli istnieją
        startTime: discountData.startTime ? new Date(discountData.startTime).toISOString() : null,
        endTime: discountData.endTime ? new Date(discountData.endTime).toISOString() : null,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create discount', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Dezaktywacja rabatu
  const deactivateDiscount = async (discountId: number): Promise<void> => {
    try {
      await api.delete(`${apiUrl}discount/${discountId}`);
    } catch (error: any) {
      console.error('Failed to deactivate discount', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  return (
    <ProductContext.Provider
      value={{
        createProduct,
        getProductsForProducer,
        getProductByName,
        getProducts,
        getProduct,
        deleteProduct,
        editProduct,
        getPopularProducts,
        getProductsWithMissingDeliveryCosts,
        // Zmodyfikowane metody do obsługi rabatów
        getProductDiscounts,
        createDiscount,
        deactivateDiscount,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
